import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"

import styles from "./styles.module.css"
import { CommentsPanelList } from "./CommentPanelList"
import { Button, getFormattedRecentDate, Spinner, Tooltip, useAppContext } from "@dit/core-frontend"
import { useEditorCommentsContext } from "."
import { Box, Text } from "@mos-cat/ds"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { ArrowDown, ArrowUp, Close } from "@mos-cat/ds-icons"
import MainComment from "./MainComment"
import { ApproveCommentIcon, GreenMark } from "../../theme/editorIcons"
import { DateTime } from "luxon"
import { CommentActions } from "./CommentActions"
import { useCallback } from "react"
import { Person } from "@src/components/general"

export const CommentsPanel = memo(
  ({
    setShowComments,
    comments,
    activeID,
    setActiveID,
    clickComment,
    markNodeMap,
    textPositionTop,
  }) => {
    const mainContainer = document.getElementById("mainContainer")

    const { isLoading, setIsLoading, approveComment, disapproveComment } =
      useEditorCommentsContext()
    const [editor] = useLexicalComposerContext()
    const { showAlert, user } = useAppContext()
    const [isEditComment, setIsEditComment] = useState(false)

    const changeIsEditComment = useCallback((value) => {
      setIsEditComment(value)
    }, [])

    const listRef = useRef(null)
    const keysArray = useMemo(() => {
      return [...markNodeMap.keys()]
    }, [markNodeMap])

    // Получаем индекс активного ID
    const initialIndex = keysArray.indexOf(activeID)
    const [currentIndex, setCurrentIndex] = useState(initialIndex)

    const handleArrowDown = () => {
      const newIndex = (currentIndex + 1) % keysArray.length
      setCurrentIndex(newIndex)
      setIsLoading(true)
      setActiveID(keysArray[newIndex])
      clickComment()
    }

    const handleArrowUp = () => {
      const newIndex = (currentIndex - 1 + keysArray.length) % keysArray.length
      setCurrentIndex(newIndex)
      setIsLoading(true)
      setActiveID(keysArray[newIndex])
      clickComment()
    }

    useEffect(() => {
      // Если activeId меняется, обновляем currentIndex
      const newIndex = keysArray.indexOf(activeID)
      if (newIndex !== -1) {
        setCurrentIndex(newIndex)
      }
    }, [activeID, keysArray])

    useEffect(() => {
      if (comments?.id) {
        setIsLoading(false)
      }
    }, [comments, isLoading, setIsLoading, showAlert])

    return (
      <Box
        marginTop={
          textPositionTop +
          mainContainer.scrollTop -
          (editor._config.namespace === "EditorPreview" ? 265 : 130)
        }
        marginBottom="20px"
        width="300px"
        position="relative"
        className={
          `${styles.CommentPlugin_CommentsPanel}` +
          (editor._config.namespace === "EditorPreview"
            ? ` ${styles.CommentPlugin_CommentsPanelPreview}`
            : "")
        }
      >
        <>
          <Box display="flex" alignItems="flex-start">
            <Box
              display="flex"
              flexDirection="column"
              styles={{
                width: "300px",
                borderLeft: `4px solid ${comments?.approved ? "#006633" : "#FFAA00"}`,
              }}
            >
              <Box
                display="flex"
                height="32px"
                flex="2"
                justifyContent="space-between"
                borderBottom="1px solid rgba(230, 233, 237, 1)"
              >
                <Box display="flex" alignItems="center" marginLeft="10px" gridGap="4px">
                  <Button kind="borderless" onClick={handleArrowDown}>
                    <ArrowDown color="#13151A" width="24px" height="24px" />
                  </Button>
                  <Button kind="borderless" onClick={handleArrowUp}>
                    <ArrowUp color="#13151A" width="24px" height="24px" />
                  </Button>
                  <Text as={Box}>
                    {currentIndex + 1} из {markNodeMap.size}
                  </Text>
                </Box>

                <Box
                  display="flex"
                  alignItems="center"
                  gridGap="7px"
                  marginRight="5px"
                  styles={{ cursor: "pointer" }}
                >
                  {comments?.approved ? (
                    <Tooltip content="Открыть комментарий">
                      <Button
                        kind="borderless"
                        onClick={() => disapproveComment(comments?.id, comments?.textBlockId)}
                      >
                        <GreenMark />
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip content="Закрыть комментарий">
                      <Button
                        kind="borderless"
                        onClick={() => approveComment(comments?.id, comments?.textBlockId)}
                      >
                        <ApproveCommentIcon />
                      </Button>
                    </Tooltip>
                  )}
                  {!comments?.approved && (
                    <CommentActions setIsEditComment={changeIsEditComment} comment={comments} />
                  )}
                  <Tooltip content="Закрыть панель комментариев">
                    <Button
                      kind="menu-button"
                      onClick={() => {
                        setShowComments(false)
                        setActiveID("")
                      }}
                    >
                      <Close width={21} height={21} />
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
              <Box className={styles.CommentPlugin_CommentsPanel_List_Details}>
                <Box className={styles.CommentPlugin_CommentsPanel_List_Comment_Author}>
                  <Person
                    person={comments?.user}
                    gridGap="5px"
                    fontSize="fs-14"
                    color="gray-80"
                    backgroundColor="inherit"
                    isProfileLink
                  />
                </Box>
                <Text fontSize="fs-14" color="gray-40">
                  {getFormattedRecentDate(DateTime.fromISO(comments?.createdAt))}
                </Text>
              </Box>

              <MainComment
                modifiedAt={comments?.modifiedAt}
                commentText={comments?.text}
                commentId={comments?.id}
                commentTextBlockId={comments?.textBlockId}
                isEditComment={isEditComment}
                setIsEditComment={changeIsEditComment}
              />
            </Box>
          </Box>

          <CommentsPanelList
            activeID={activeID}
            comments={comments?.reply?.sort((a, b) =>
              DateTime.fromISO(a.createdAt) < DateTime.fromISO(b.createdAt) ? -1 : 1,
            )}
            mainComment={comments}
            listRef={listRef}
            markNodeMap={markNodeMap}
            setShowComments={setShowComments}
            clickComment={clickComment}
            textPositionTop={textPositionTop}
            setIsEditComment={changeIsEditComment}
          />
        </>
      </Box>
    )
  },
)

CommentsPanel.displayName = "CommentsPanelComp"
